import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useStudyGroups, type GroupTopicOfDay } from "@/hooks/use-study-groups";
import { queryKeys } from "@/lib/query-keys";

export interface GroupTopicOfDayWithGroup extends GroupTopicOfDay {
    groupId: string;
    groupName: string;
    /** Copied from the membership row so the card can show the same avatar
     *  as the study-groups list instead of a generic glyph. */
    groupAvatarUrl: string | null;
    groupIcon: string | null;
}

export function useGroupTopicsOfDay(options: { enabled?: boolean } = {}) {
    const { enabled = true } = options;
    const { data: isAuthenticated, userId } = useAuth();
    const shouldLoadGroups = enabled && !!isAuthenticated && !!userId;
    const { myGroups, getGroupTopicOfDay, loading: groupsLoading } = useStudyGroups(
        shouldLoadGroups ? userId : null,
        { includePublicGroups: false, includeNotifications: false },
    );

    return useQuery({
        queryKey: queryKeys.groupTopicOfDay.byUser(userId),
        enabled: shouldLoadGroups && !groupsLoading && (myGroups?.length ?? 0) > 0,
        queryFn: async (): Promise<GroupTopicOfDayWithGroup[]> => {
            if (!myGroups || myGroups.length === 0) return [];

            const topicPromises = myGroups.map(async (group) => {
                const topicOfDay = await getGroupTopicOfDay(group.id);
                if (topicOfDay) {
                    return {
                        ...topicOfDay,
                        groupId: group.id,
                        groupName: group.name,
                        groupAvatarUrl: group.avatar_url ?? null,
                        groupIcon: group.icon ?? null,
                    } as GroupTopicOfDayWithGroup;
                }
                return null;
            });

            const topics = await Promise.all(topicPromises);
            const validTopics = topics.filter((t): t is GroupTopicOfDayWithGroup => t !== null);

            // Most recently set first.
            validTopics.sort((a, b) => {
                const dateA = new Date(a.set_at).getTime();
                const dateB = new Date(b.set_at).getTime();
                return dateB - dateA;
            });

            return validTopics;
        },
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
        placeholderData: (previousData) => previousData,
        refetchOnWindowFocus: false,
    });
}
